import { useState, useCallback, useMemo, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

const STORAGE_KEY = 'rv-user-preferences'

export interface UserPreferences {
  dateFormat: 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd'
  timeFormat: '12h' | '24h'
  timezone: string // IANA timezone string
}

const DEFAULTS: UserPreferences = {
  dateFormat: 'dd/MM/yyyy',
  timeFormat: '24h',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

function loadLocal(): UserPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return { ...DEFAULTS, ...JSON.parse(stored) }
  } catch { /* ignore */ }
  return { ...DEFAULTS }
}

function saveLocal(prefs: UserPreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

export function usePreferences() {
  const { user } = useAuth()
  const [prefs, setPrefs] = useState<UserPreferences>(loadLocal)
  const [loaded, setLoaded] = useState(false)

  // Load from Supabase on login
  useEffect(() => {
    if (!user || loaded) return
    supabase
      .from('user_preferences')
      .select('date_format, time_format, timezone')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          const remote: UserPreferences = {
            dateFormat: (data.date_format as UserPreferences['dateFormat']) ?? DEFAULTS.dateFormat,
            timeFormat: (data.time_format as UserPreferences['timeFormat']) ?? DEFAULTS.timeFormat,
            timezone: (data.timezone as string) ?? DEFAULTS.timezone,
          }
          setPrefs(remote)
          saveLocal(remote)
        }
        setLoaded(true)
      })
  }, [user, loaded])

  const update = useCallback((patch: Partial<UserPreferences>) => {
    setPrefs(prev => {
      const next = { ...prev, ...patch }
      saveLocal(next)

      // Persist to Supabase
      if (user) {
        supabase
          .from('user_preferences')
          .upsert({
            user_id: user.id,
            date_format: next.dateFormat,
            time_format: next.timeFormat,
            timezone: next.timezone,
            updated_at: new Date().toISOString(),
          })
          .then(() => { /* fire and forget */ })
      }

      return next
    })
  }, [user])

  const formatDate = useCallback((date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    const opts: Intl.DateTimeFormatOptions = { timeZone: prefs.timezone }

    if (prefs.dateFormat === 'dd/MM/yyyy') {
      opts.day = '2-digit'; opts.month = '2-digit'; opts.year = 'numeric'
      return d.toLocaleDateString('en-GB', opts)
    } else if (prefs.dateFormat === 'MM/dd/yyyy') {
      opts.day = '2-digit'; opts.month = '2-digit'; opts.year = 'numeric'
      return d.toLocaleDateString('en-US', opts)
    } else {
      const parts = new Intl.DateTimeFormat('en-CA', { ...opts, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d)
      const y = parts.find(p => p.type === 'year')?.value
      const m = parts.find(p => p.type === 'month')?.value
      const dy = parts.find(p => p.type === 'day')?.value
      return `${y}-${m}-${dy}`
    }
  }, [prefs.dateFormat, prefs.timezone])

  const formatTime = useCallback((date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleTimeString(prefs.timeFormat === '12h' ? 'en-US' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: prefs.timeFormat === '12h',
      timeZone: prefs.timezone,
    })
  }, [prefs.timeFormat, prefs.timezone])

  const formatDateTime = useCallback((date: Date | string) => {
    return `${formatDate(date)} ${formatTime(date)}`
  }, [formatDate, formatTime])

  const formatTimeShort = useCallback((date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleTimeString(prefs.timeFormat === '12h' ? 'en-US' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: prefs.timeFormat === '12h',
      timeZone: prefs.timezone,
    })
  }, [prefs.timeFormat, prefs.timezone])

  const timezoneLabel = useMemo(() => {
    try {
      const fmt = new Intl.DateTimeFormat('en', { timeZone: prefs.timezone, timeZoneName: 'short' })
      const parts = fmt.formatToParts(new Date())
      return parts.find(p => p.type === 'timeZoneName')?.value ?? prefs.timezone
    } catch { return prefs.timezone }
  }, [prefs.timezone])

  return { prefs, update, formatDate, formatTime, formatDateTime, formatTimeShort, timezoneLabel }
}
